import { Chip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CheckCircleOutlineRounded,
  ErrorOutlineRounded
} from '@material-ui/icons';
import moment from 'moment';
import { WorkflowStatusLabel } from '../pages/ProjectDocumentRevision/DocumentRevisionView';
import { DocumentWorkflowWithStepAndApproval } from '../pages/ProjectDocumentWorkflow/projectDocumentWorkflowSlice';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      margin: theme.spacing(0, -1),
      border: '1px solid',
      borderColor: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer'
    },
    successColor: {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    errorColor: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    successOutlineColor: {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.success.main
    },
    warningOutlineColor: {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.warning.main
    },
    errorOutlineColor: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.error.main
    }
  })
);

interface StatusChipProps {
  workflowWithStep?: DocumentWorkflowWithStepAndApproval;
  planIssueDate: Date; //TODO: check type of planIssueDate can be string?
  actualIssueDate?: Date | null;
}

function getClassNameChipIcon(
  classes: ReturnType<typeof useStyles>,
  workflowWithStep?: DocumentWorkflowWithStepAndApproval,
  dueDate?: Date | string, //TODO: check type of planIssueDate can be string?
  isDelayIssue?: boolean
) {
  const classNames = [classes.chip];
  let chipIcon;
  const hasWorkflow = workflowWithStep;
  const isInProgressWorkflow =
    workflowWithStep &&
    workflowWithStep.workflowStatusId === WorkflowStatusLabel.InProgress;

  if ((!hasWorkflow || isInProgressWorkflow) && dueDate) {
    const isDelay = moment(dueDate) < moment();
    classNames.push(isDelay ? classes.errorColor : classes.successColor);
  }
  if (
    workflowWithStep &&
    workflowWithStep.workflowStatusId === WorkflowStatusLabel.Completed
  ) {
    if (isDelayIssue) {
      classNames.push(
        isDelayIssue ? classes.warningOutlineColor : classes.successOutlineColor
      );
      chipIcon = (
        <CheckCircleOutlineRounded
          className={
            isDelayIssue
              ? classes.warningOutlineColor
              : classes.successOutlineColor
          }
        />
      );
    } else {
      classNames.push(classes.successOutlineColor);
      chipIcon = (
        <CheckCircleOutlineRounded className={classes.successOutlineColor} />
      );
    }
  }
  if (
    workflowWithStep &&
    workflowWithStep.workflowStatusId === WorkflowStatusLabel.Rejected
  ) {
    classNames.push(classes.errorOutlineColor);
    chipIcon = <ErrorOutlineRounded className={classes.errorOutlineColor} />;
  }
  return { className: classNames.join(' '), chipIcon };
}

export default function StatusChip({
  workflowWithStep,
  planIssueDate,
  actualIssueDate
}: StatusChipProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  let activityStatus;
  let dueDate;
  let finishedDate;
  let isDelayIssue;

  if (!workflowWithStep) {
    activityStatus = t('statusChip.waitForIssue');
    dueDate = planIssueDate;
  } else if (
    workflowWithStep.workflowStatusId === WorkflowStatusLabel.InProgress
  ) {
    activityStatus =
      t('statusChip.waitFor') + ' ' + workflowWithStep.currentWorkflowStep.workflowStep.name;
    dueDate = workflowWithStep.currentWorkflowStep.dueDate;
  } else if (
    actualIssueDate &&
    new Date(actualIssueDate) > new Date(planIssueDate) &&
    workflowWithStep.workflowStatusId === WorkflowStatusLabel.Completed
  ) {
    activityStatus = `${workflowWithStep.workflowStatus.name} (${t('statusChip.delay')})`;
    finishedDate = workflowWithStep.finishedDate;
  } else {
    activityStatus = workflowWithStep.workflowStatus.name;
    finishedDate = workflowWithStep.finishedDate;
  }

  if (actualIssueDate) {
    isDelayIssue = new Date(actualIssueDate) > new Date(planIssueDate);
  }

  return (
    <div>
      <Chip
        className={
          getClassNameChipIcon(classes, workflowWithStep, dueDate, isDelayIssue)
            .className
        }
        size="small"
        label={activityStatus}
        icon={
          getClassNameChipIcon(classes, workflowWithStep, dueDate, isDelayIssue)
            .chipIcon
        }
      />
      {dueDate && (
        <Typography variant="subtitle2" color="textSecondary">
          {t('statusChip.dueDate')}: {moment(dueDate).format('ll')}
        </Typography>
      )}
      {finishedDate && (
        <Typography variant="subtitle2" color="textSecondary">
          {t('statusChip.end')}: {moment(finishedDate).format('lll')}
        </Typography>
      )}
    </div>
  );
}
